import React, { useCallback } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { updateRecurringGift } from '@/data/giving';
import RecurringGiftList from '@/components/Giving/RecurringGiftList';

export default function ManageRecurringGiftsModal({ isOpen, onClose, organizationID, personID }) {
    const { theme: { colors } } = useUnistyles();
    const queryClient = useQueryClient();

    const { data: recurringGifts, isFetching } = Query.giving.recurringGifts({ organizationID, personID });
    const { data: funds } = Query.contributionFunds.all();

    const updateMutation = useMutation({
        mutationFn: ({ recurringGiftID, status }) => updateRecurringGift({ recurringGiftID, status }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.recurringGifts(organizationID, personID) });
            Toast.show({ type: 'success', text2: 'Recurring gift updated' });
        },
        onError: (error) => {
            Toast.show({ type: 'error', text2: error?.message || 'Failed to update recurring gift' });
        },
    });

    const handleUpdate = useCallback((gift, status) => {
        updateMutation.mutate({ recurringGiftID: gift.id, status });
    }, [updateMutation]);

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            onCancel={onClose}
            title="Recurring Gifts"
            subtitle="Manage your scheduled gifts"
        >
            <View style={styles.container} testID="manage-recurring-gifts">
                {isFetching ? (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator color={colors.giving} />
                    </View>
                ) : (
                    <RecurringGiftList
                        testID="manage-recurring-gifts-list"
                        gifts={recurringGifts || []}
                        funds={funds || []}
                        onUpdate={handleUpdate}
                    />
                )}
            </View>
        </SheetModal>
    );
}

const styles = StyleSheet.create(({ spacing }) => ({
    container: {
        gap: spacing.sm,
        padding: spacing.sm,
    },
    loadingContainer: {
        alignItems: 'center',
        paddingVertical: spacing.xl,
    },
}));
